import { Alert, Button, Progress, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router';
import type { TemplateRunItemDto } from '@pams/shared';
import { StatusDot } from '@/components/StatusDot';
import { istDateTime } from '@/lib/analyticsReport';
import { TEMPLATE_RUN_OUTCOME_LABEL, TEMPLATE_RUN_OUTCOME_TONE } from '@/lib/labels';
import { orderStatusWord } from '@/lib/statusPresentation';
import { RUN_COPY } from '../copy';
import { useTemplateRun } from '../useTemplateRun';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4d/§4f] AN APPROVED RUN — ITS PROGRESS WHILE IT RUNS, AND ITS REPORT WHEN IT IS DONE.
 *
 *  ⚠️ THE REPORT IS BUILT FROM THE RUN'S ITEMS, WHICH THE SERVER READS BACK FROM THE ORDERS
 *  THEMSELVES — "a report of a write states what was written". An item whose order exists says the
 *  order's status NOW, through `orderStatusWord` (the one word for a status on every surface), and
 *  links to it; a failed or skipped item says why in the server's own sentence.
 *
 *  ⚠️ ONE COMPONENT FOR BOTH RUN KINDS. A create run's items are templates and their parts; a publish
 *  run's are orders. The item shape is one shape (`templateRunItemSchema`), so the report is too.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export const RunProgress = ({ runId }: { runId: string }): React.ReactElement => {
  const { run, isLoading, loadError, error, following, resume } = useTemplateRun(runId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }
  if (!run) return <Alert type="error" showIcon message={loadError ?? RUN_COPY.failedRequest} />;

  const finished = run.counts.total - run.counts.pending;
  const percent = run.counts.total === 0 ? 100 : Math.round((finished / run.counts.total) * 100);
  const created = run.items.filter((item) => item.outcome === 'CREATED' || item.outcome === 'PUBLISHED').length;
  const already = run.items.filter((item) => item.outcome === 'ALREADY_DONE').length;

  const columns: ColumnsType<TemplateRunItemDto> = [
    {
      key: 'title',
      title: '',
      render: (_value, item) => (
        <span className="flex flex-col">
          {/* [DP2026] A planned title already ends "(part N of M)" — it is the draft's own title, shown as is. */}
          <span>{item.title}</span>
          {item.templateKey ? <span className="text-xs text-muted-foreground">{item.templateKey}</span> : null}
        </span>
      ),
    },
    {
      key: 'outcome',
      title: '',
      render: (_value, item) => (
        <StatusDot
          tone={TEMPLATE_RUN_OUTCOME_TONE[item.outcome]}
          label={TEMPLATE_RUN_OUTCOME_LABEL[item.outcome]}
          data-testid="run-item-outcome"
        />
      ),
    },
    {
      key: 'order',
      title: '',
      render: (_value, item) =>
        item.arrangementId === null ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="flex items-center gap-2">
            <Link to={`/arrangements/${item.arrangementId}`} className="text-link" data-testid="run-item-link">
              {item.reference ?? RUN_COPY.open}
            </Link>
            {item.status === null ? null : (
              <span className="text-xs text-muted-foreground">
                {orderStatusWord({ status: item.status, archivedAt: null }).label}
              </span>
            )}
          </span>
        ),
    },
    {
      key: 'reason',
      title: '',
      render: (_value, item) => (item.reason ? <span className="text-muted-foreground">{item.reason}</span> : null),
    },
  ];

  return (
    <section className="flex flex-col gap-3" data-testid="run-progress" data-state={run.state}>
      <div className="flex flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3 text-sm">
        <Progress percent={percent} status={run.state === 'DONE' ? 'success' : 'active'} showInfo={false} />
        <span data-testid="run-progress-count">{RUN_COPY.progress(finished, run.counts.total)}</span>
        <span className="text-muted-foreground">
          {run.state === 'DONE' ? RUN_COPY.done : following ? RUN_COPY.leaseHeld : RUN_COPY.running}
        </span>
        <span className="text-xs text-muted-foreground">
          {RUN_COPY.approvedBy(run.approvedBy.name, istDateTime(run.approvedAt))}
        </span>
      </div>
      {error ? (
        <Alert
          type="error"
          showIcon
          data-testid="run-error"
          message={`${RUN_COPY.failedRequest} ${error}`}
          action={
            <Button size="small" onClick={resume}>
              {RUN_COPY.retry}
            </Button>
          }
        />
      ) : null}
      <h2 className="text-sm font-medium text-heading">{RUN_COPY.reportTitle}</h2>
      <p className="text-sm" data-testid="run-report-counts">
        {RUN_COPY.counts(created, already, run.counts.failed, run.counts.skipped)}
      </p>
      <Table
        rowKey={(item) => item.key}
        columns={columns}
        dataSource={run.items}
        size="small"
        showHeader={false}
        pagination={run.items.length > 50 ? { pageSize: 50 } : false}
        scroll={{ x: 'max-content' }}
        data-testid="run-items"
      />
    </section>
  );
};
